import React, { useState } from "react";
import { useAction } from "convex/react";
import { api } from "../../../../../backend/convex/_generated/api";
import { useOrganization } from "../../hooks/useOrganization";
import { OrganizationAdminLayout } from "../../components/OrganizationAdminLayout";
import { AlertModal } from "../../components/Modal";

export const Billing: React.FC = () => {
  const { organization, isAdmin, isLoading } = useOrganization();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: "",
  });
  const createCheckoutSession = useAction(api.stripe.createCheckoutSession);
  const createCustomerPortal = useAction(api.stripe.createCustomerPortal);

  if (isLoading) {
    return (
      <OrganizationAdminLayout title="Billing">
        <div className="p-8">Loading...</div>
      </OrganizationAdminLayout>
    );
  }

  if (!organization || !isAdmin) {
    return (
      <OrganizationAdminLayout title="Billing">
        <div className="p-8 text-red-500">Unauthorized</div>
      </OrganizationAdminLayout>
    );
  }

  const handleUpgrade = async () => {
    try {
      setIsRedirecting(true);
      const checkoutUrl = await createCheckoutSession({
        orgId: organization._id,
        priceId: "price_placeholder_pro",
      });
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      setError({
        isOpen: true,
        message: error instanceof Error ? error.message : "Failed to start checkout. Please try again.",
      });
    } finally {
      setIsRedirecting(false);
    }
  };

  const handleManage = async () => {
    try {
      setIsRedirecting(true);
      const portalUrl = await createCustomerPortal({
        orgId: organization._id,
      });
      if (portalUrl) {
        window.location.href = portalUrl;
      }
    } catch (error) {
      console.error("Failed to create portal session:", error);
      setError({
        isOpen: true,
        message: error instanceof Error ? error.message : "Failed to open billing portal. Please try again.",
      });
    } finally {
      setIsRedirecting(false);
    }
  };

  return (
    <OrganizationAdminLayout title="Billing & Subscription">
      <AlertModal
        isOpen={error.isOpen}
        onClose={() => setError({ isOpen: false, message: "" })}
        title="Error"
        message={error.message}
        type="error"
      />
      <div className="max-w-4xl">
        <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold mb-1">Current Plan</h2>
                <p className="text-muted-foreground text-sm">
                  Manage your organization's subscription and billing details.
                </p>
              </div>
              <div className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium capitalize">
                {organization.plan || "Free"}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center py-4 border-t border-border">
                <div>
                  <p className="font-medium">Plan Details</p>
                  <p className="text-sm text-muted-foreground">
                    {organization.plan === "pro" 
                      ? "Professional plan with advanced features" 
                      : "Free plan for individuals and small teams"}
                  </p>
                </div>
                {organization.plan !== "pro" && (
                  <button
                    onClick={handleUpgrade}
                    disabled={isRedirecting}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {isRedirecting ? "Redirecting..." : "Upgrade to Pro"}
                  </button>
                )}
              </div>

              {organization.stripeCustomerId && (
                <div className="flex justify-between items-center py-4 border-t border-border">
                  <div>
                    <p className="font-medium">Billing Portal</p>
                    <p className="text-sm text-muted-foreground">
                      Update payment methods and download invoices.
                    </p>
                  </div>
                  <button
                    onClick={handleManage}
                    disabled={isRedirecting}
                    className="bg-secondary hover:bg-secondary/80 text-secondary-foreground px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {isRedirecting ? "Redirecting..." : "Manage Subscription"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 bg-card rounded-xl border border-border shadow-sm">
            <h3 className="font-semibold mb-2">Usage Tracking</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Track your organization's usage across all workspaces.
            </p>
            <div className="text-2xl font-bold">Coming Soon</div>
          </div>
        </div>
      </div>
    </OrganizationAdminLayout>
  );
};


